import { useEffect, useRef, useState } from 'react';
import { MAX_RADIUS } from '../lib/constants';
import { encodeGeohash } from '../lib/geo';
import {
  hasMapsKey,
  loadGoogleMaps,
  googleMapStyles,
  resolvePlaceId,
  reverseGeocode,
  searchPlaces,
} from '../lib/maps';
import Icon from './Icon';

export default function LocationPicker({
  open,
  onClose,
  initial,
  radiusMiles = 50,
  showRadius = true,
  title = 'Location',
  confirmLabel = 'Apply',
  onConfirm,
}) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markerObj = useRef(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [place, setPlace] = useState(initial || null);
  const [radius, setRadius] = useState(radiusMiles);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const mapsReady = hasMapsKey();

  useEffect(() => {
    if (!open) return;
    setPlace(initial || null);
    setRadius(radiusMiles);
    setQuery('');
    setSuggestions([]);
    setError('');
  }, [open, initial, radiusMiles]);

  useEffect(() => {
    if (!open || !mapsReady || !mapRef.current) return undefined;
    let cancelled = false;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapRef.current) return;
        const center = place
          ? { lat: place.lat, lng: place.lng }
          : { lat: 39.8283, lng: -98.5795 };
        if (!mapObj.current) {
          mapObj.current = new maps.Map(mapRef.current, {
            center,
            zoom: place ? 11 : 4,
            disableDefaultUI: true,
            zoomControl: true,
            clickableIcons: false,
            styles: googleMapStyles(),
          });
          mapObj.current.addListener('click', async (e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            setBusy(true);
            try {
              const next = await reverseGeocode(lat, lng);
              setPlace(next);
            } catch {
              setPlace({ lat, lng, city: '', state: '', label: 'Selected location' });
            } finally {
              setBusy(false);
            }
          });
        } else {
          mapObj.current.setCenter(center);
          mapObj.current.setZoom(place ? 11 : 4);
        }

        if (place) {
          if (!markerObj.current) {
            markerObj.current = new maps.Marker({
              map: mapObj.current,
              position: { lat: place.lat, lng: place.lng },
            });
          } else {
            markerObj.current.setPosition({ lat: place.lat, lng: place.lng });
            markerObj.current.setMap(mapObj.current);
          }
          mapObj.current.panTo({ lat: place.lat, lng: place.lng });
        }
      })
      .catch((err) => setError(err.message));

    return () => {
      cancelled = true;
    };
  }, [open, mapsReady, place?.lat, place?.lng]);

  useEffect(() => {
    if (!open || !mapsReady) return undefined;
    if (!query.trim()) {
      setSuggestions([]);
      return undefined;
    }
    const t = setTimeout(() => {
      searchPlaces(query)
        .then(setSuggestions)
        .catch(() => setSuggestions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query, open, mapsReady]);

  if (!open) return null;

  const locateMe = () => {
    if (!navigator.geolocation) {
      setError('Location is not available in this browser.');
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const next = mapsReady
            ? await reverseGeocode(pos.coords.latitude, pos.coords.longitude)
            : {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                city: '',
                state: '',
                label: 'Near you',
              };
          setPlace(next);
        } catch {
          setPlace({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            city: '',
            state: '',
            label: 'Near you',
          });
        } finally {
          setBusy(false);
        }
      },
      () => {
        setBusy(false);
        setError('Could not get your current location.');
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const pickSuggestion = async (item) => {
    setBusy(true);
    setQuery('');
    setSuggestions([]);
    try {
      const next = await resolvePlaceId(item.placeId);
      setPlace(next);
    } catch {
      setError('Could not resolve that place.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => {
    if (!place?.lat || !place?.lng) {
      setError('Pick a location on the map or search for a city.');
      return;
    }
    onConfirm?.({
      ...place,
      geohash: encodeGeohash(place.lat, place.lng),
      radiusMiles: showRadius ? radius : undefined,
    });
    onClose?.();
  };

  const placeLabel = place?.label || 'Nearby';

  return (
    <div className="sheet-root" role="presentation">
      <button type="button" className="sheet-backdrop" aria-label="Close" onClick={onClose} />
      <div className="sheet location-sheet" role="dialog" aria-modal="true" aria-label={title}>
        <header className="sheet-head location-sheet-head">
          <button type="button" className="icon-button" onClick={onClose} aria-label="Cancel">
            <Icon name="close" size={22} />
          </button>
          <h2>{title}</h2>
          <span className="location-sheet-head-spacer" aria-hidden="true" />
        </header>

        <div className="location-search">
          <Icon name="search" size={18} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={mapsReady ? 'Search city or place' : 'Enable Maps to search places'}
            disabled={!mapsReady}
          />
        </div>

        {suggestions.length > 0 && (
          <ul className="place-suggestions">
            {suggestions.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => pickSuggestion(s)}>
                  <Icon name="mapPin" size={16} />
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="location-map-wrap">
          {mapsReady ? (
            <div className="location-map" ref={mapRef} />
          ) : (
            <div className="location-map fallback">
              <Icon name="map" size={32} />
              <p>Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to enable the map picker.</p>
            </div>
          )}
          <button type="button" className="location-locate-chip" onClick={locateMe} disabled={busy}>
            <Icon name="crosshair" size={16} />
            Locate
          </button>
        </div>

        <div className="location-selected">
          <Icon name="mapPin" size={16} />
          <b>{placeLabel}</b>
        </div>

        {showRadius && (
          <div className="radius-slider-row">
            <div className="radius-slider-head">
              <span>Browse radius</span>
              <strong>{radius} mi</strong>
            </div>
            <input
              type="range"
              className="radius-slider"
              min={5}
              max={MAX_RADIUS}
              step={5}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              aria-label="Browse radius in miles"
            />
          </div>
        )}

        {!showRadius && (
          <p className="location-sheet-note">Buyers only see city and state.</p>
        )}

        {error && <p className="form-error">{error}</p>}

        <button
          type="button"
          className="primary-button full location-sheet-apply"
          onClick={confirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}
